import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getSessionApi } from "../../../utils/demoApi";
import { resolveSessionKind } from "../sessionKind";
import DemoPublicShell from "./DemoPublicShell";
import LobbyExtras from "./LobbyExtras";
import useAttendeeToken from "./useAttendeeToken";
import "../demo-classes.scss";

const POLL_INTERVAL_MS = 12000;

const formatCountdown = (ms) => {
  if (ms <= 0) return "00:00:00";
  const total = Math.floor(ms / 1000);
  const h = String(Math.floor(total / 3600)).padStart(2, "0");
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
};

/**
 * After registration, the registrant lands here. We poll the public
 * session endpoint and redirect them to /live as soon as the host opens the
 * room. Serves both live-session products — `kind` picks the words and the
 * URL tree.
 */
const DemoLobbyPage = ({ kind = "demo" }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const vocab = resolveSessionKind(kind);
  const api = getSessionApi(vocab.kind);
  // Every public page absorbs `?t=`, not just /join — a link forwarded or
  // bookmarked mid-flow has to keep working.
  const attendeeToken = useAttendeeToken(vocab.kind, slug);

  const [session, setSession] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [error, setError] = useState("");
  const intervalRef = useRef(null);

  // Make sure they actually registered first.
  useEffect(() => {
    if (!attendeeToken) {
      navigate(`${vocab.publicBase}/${slug}/register`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, navigate, kind, attendeeToken]);

  // Tick once a second for the countdown
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Poll session status
  useEffect(() => {
    const fetchOnce = async () => {
      try {
        const { data } = await api.getSession(slug);
        setSession(data);
        if (data.status === "live") {
          navigate(`${vocab.publicBase}/${slug}/live`, { replace: true });
        }
      } catch {
        setError("Connection problem. Retrying…");
      }
    };
    fetchOnce();
    intervalRef.current = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, navigate, kind]);

  if (!session) {
    return (
      <DemoPublicShell kind={vocab.kind}>
        <p className="demo-public__pulse">Connecting…</p>
        {error && <p className="demo-public__error">{error}</p>}
      </DemoPublicShell>
    );
  }

  if (session.status === "ended") {
    return (
      <DemoPublicShell kind={vocab.kind} institute={session.institute}>
        <h2 className="demo-public__title">{session.title}</h2>
        <p className="demo-public__subtitle">
          This {vocab.noun} has just ended. Thanks for stopping by!
        </p>
      </DemoPublicShell>
    );
  }
  if (session.status === "cancelled") {
    return (
      <DemoPublicShell kind={vocab.kind} institute={session.institute}>
        <h2 className="demo-public__title">{session.title}</h2>
        <p className="demo-public__subtitle">
          This {vocab.noun} has been cancelled. We'll reach out if it's rescheduled.
        </p>
      </DemoPublicShell>
    );
  }

  const start = session.scheduledStartAt ? new Date(session.scheduledStartAt).getTime() : null;
  const remaining = start ? start - now : 0;

  return (
    <DemoPublicShell kind={vocab.kind} institute={session.institute}>
      <h2 className="demo-public__title">{session.title}</h2>
      <p className="demo-public__subtitle">You're in! We'll redirect you automatically when the host opens the room.</p>

      <div className="demo-public__lobby">
        {remaining > 0 ? (
          <>
            <div className="demo-public__lobby-label">{vocab.Noun} starts in</div>
            <div className="demo-public__countdown">{formatCountdown(remaining)}</div>
            <span className="demo-public__pulse">Waiting for the host…</span>
          </>
        ) : (
          <>
            <div className="demo-public__countdown">Almost ready</div>
            <span className="demo-public__pulse">Waiting for the host to start the room…</span>
          </>
        )}
      </div>
      <LobbyExtras api={api} slug={slug} token={attendeeToken} calendarUrl={api.calendarUrl(slug)} />
    </DemoPublicShell>
  );
};

export default DemoLobbyPage;
